import { styled } from '@mui/material';

import { CheckIcon } from '../../../icons';

export type StepTrackerStep = {
  id: string;
  label: string;
  completed: boolean;
};

type Props = {
  steps: StepTrackerStep[];
  activeIndex: number;
  onSelect: (index: number) => void;
  'aria-label': string;
  'data-testid'?: string;
};

type StepState = 'active' | 'completed' | 'upcoming';

const NODE_SIZE = 28;
const CONNECTOR_WIDTH = 2;

const List = styled('ol')(({ theme }) => ({
  display: 'flex',
  margin: 0,
  padding: theme.spacing(1.5, 1),
  listStyle: 'none',
  overflowX: 'auto',
  scrollbarWidth: 'none',
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const Item = styled('li')<{ ownerState: { state: StepState } }>(({ theme, ownerState }) => ({
  position: 'relative',
  flex: '1 1 0',
  minWidth: theme.spacing(8),
  '&:not(:first-of-type)::before': {
    content: '""',
    position: 'absolute',
    top: NODE_SIZE / 2 - CONNECTOR_WIDTH / 2,
    right: `calc(50% + ${NODE_SIZE / 2 + 4}px)`,
    left: `calc(-50% + ${NODE_SIZE / 2 + 4}px)`,
    height: CONNECTOR_WIDTH,
    borderRadius: CONNECTOR_WIDTH,
    backgroundColor: ownerState.state === 'upcoming' ? theme.palette.divider : theme.palette.done.main,
  },
}));

const StepButton = styled('button')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  width: '100%',
  minHeight: 44,
  margin: 0,
  padding: 0,
  border: 0,
  background: 'none',
  font: 'inherit',
  color: 'inherit',
  cursor: 'pointer',
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
    borderRadius: theme.shape.borderRadius,
  },
}));

const Node = styled('span')<{ ownerState: { state: StepState } }>(({ theme, ownerState }) => ({
  ...theme.typography.body2Emphasis,
  position: 'relative',
  zIndex: 1,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: NODE_SIZE,
  height: NODE_SIZE,
  borderRadius: '50%',
  fontVariantNumeric: 'tabular-nums',
  ...(ownerState.state === 'active' && {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  }),
  ...(ownerState.state === 'completed' && {
    backgroundColor: theme.palette.done.main,
    color: theme.palette.common.white,
  }),
  ...(ownerState.state === 'upcoming' && {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
  }),
}));

const Label = styled('span')<{ ownerState: { state: StepState } }>(({ theme, ownerState }) => ({
  ...theme.typography.caption,
  fontWeight: ownerState.state === 'active' ? theme.typography.fontWeightBold : undefined,
  color: ownerState.state === 'upcoming' ? theme.palette.text.secondary : theme.palette.text.primary,
  maxWidth: '100%',
  paddingInline: theme.spacing(0.5),
  textAlign: 'center',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

function getStepState(index: number, activeIndex: number, completed: boolean): StepState {
  if (index === activeIndex) return 'active';
  return completed ? 'completed' : 'upcoming';
}

/** Compact horizontal progress through a non-linear sequence of steps; every step stays reachable. */
export function StepTracker(props: Props) {
  return (
    <nav aria-label={props['aria-label']} data-testid={props['data-testid'] ?? 'step-tracker'}>
      <List>
        {props.steps.map((step, index) => {
          const state = getStepState(index, props.activeIndex, step.completed);

          return (
            <Item key={step.id} ownerState={{ state }}>
              <StepButton
                type="button"
                title={step.label}
                aria-current={state === 'active' ? 'step' : undefined}
                onClick={function handleSelect() {
                  props.onSelect(index);
                }}
              >
                <Node ownerState={{ state }} aria-hidden="true">
                  {state === 'completed' ? <CheckIcon fontSize="small" /> : index + 1}
                </Node>
                <Label ownerState={{ state }}>
                  {step.label}
                  {state === 'completed' ? <VisuallyHidden> (terminée)</VisuallyHidden> : null}
                </Label>
              </StepButton>
            </Item>
          );
        })}
      </List>
    </nav>
  );
}

const VisuallyHidden = styled('span')({
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
});

export type { Props as StepTrackerProps };
